import React, { useState, useEffect } from 'react';
import { shape, string, bool, func } from 'prop-types';
import { validate as validator } from 'ui-validator';
import { PLTextField, SentilinkAgreement } from '@shared_modules/local-propulsion/components';
import { Page } from '../../components';
import validations from './ssn-validations';
import { truncateLongName, submitHeapTracking } from '../../utils/util';
import { heapTrackEvent, heapTrackErrors } from '@shared_modules/baui-heap-tracking';

const SSN = ({
  primarySSNRequired,
  secondarySSNRequired,
  isJoint,
  primaryName,
  secondaryName,
  showSentilinkSSNConsent,
  saveValues,
  nextLocation,
}) => {
  const [ssn, setSSN] = useState('');
  const [ssnSecondary, setSSNSecondary] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [sentilinkConsent, setSentilinkConsent] = useState(false);

  useEffect(() => {
    heapTrackEvent('PL_SSN_Unhappy_PI1_Render', {
      IS_SSN_SENTILINK_CONSENT_SHOWN: showSentilinkSSNConsent,
    });
  }, []);

  const getHeaderText = () => {
    let addressee;
    let plural = '';
    if (!isJoint) {
      addressee = 'your';
    } else if (primarySSNRequired && secondarySSNRequired) {
      addressee = 'either of your';
      plural = 's';
    } else if (primarySSNRequired) {
      addressee = `${truncateLongName(primaryName.firstName)}'s`;
    } else {
      addressee = `${truncateLongName(secondaryName.firstName)}'s`;
    }
    return `We weren’t able to find ${addressee} credit report${plural} with the details you shared so far. Please enter your Social Security Number${plural} so we can try again.`;
  };

  const validateInput = fieldName => {
    // extracting out other errors than error for the current field
    const { [fieldName]: omit, ...restOfValidationErrors } = validationErrors;
    // Model would have ssn / ssnSecondary based on primarySSNRequired and secondarySSNRequired respectively
    const model = {};
    if (primarySSNRequired) model.ssn = ssn;
    if (secondarySSNRequired) model.ssnSecondary = ssnSecondary;

    const errors = validator(model, validations(fieldName));
    if (fieldName) {
      const mergedErrors = { ...restOfValidationErrors, ...errors };
      return mergedErrors;
    }

    return errors;
  };

  const handleBlur = event => setValidationErrors(validateInput(event.target.name));

  const onSentilinkChange = () => setSentilinkConsent(checkboxPrevState => !checkboxPrevState);

  const submit = e => {
    if (e) e.preventDefault();
    submitHeapTracking(isJoint);

    const isSSNSentilinkConsentChecked = showSentilinkSSNConsent
      ? { IS_SSN_SENTILINK_CONSENT_CHECKED: sentilinkConsent }
      : undefined;
    heapTrackEvent('PL_SSN_Unhappy_PI1_Submit', {
      IS_SSN_SENTILINK_CONSENT_SHOWN: showSentilinkSSNConsent,
      ...isSSNSentilinkConsentChecked,
    });

    const errors = validateInput();

    if (!Object.keys(errors).length > 0) {
      // Model would have ssn / ssnSecondary based on primarySSNRequired and secondarySSNRequired respectively
      const model = {};
      if (primarySSNRequired) model.ssn = ssn;
      if (secondarySSNRequired) model.ssnSecondary = ssnSecondary;
      if (showSentilinkSSNConsent) model.sentilinkConsent = sentilinkConsent;

      saveValues({ ...model });
      nextLocation();
    } else {
      // Model would have ssn / ssnSecondary based on primarySSNRequired and secondarySSNRequired respectively
      setValidationErrors(errors);
      heapTrackErrors(errors);
    }
  };

  const isSubmitBtnDisabled = showSentilinkSSNConsent && !!(!sentilinkConsent || validationErrors.ssn);

  return (
    <Page
      title="We need a little more information."
      header={getHeaderText()}
      name="ssn"
      submitHandler={submit}
      submitLabel="Get Your Rate"
      disableSubmit={isSubmitBtnDisabled}
    >
      <div className="form__input">
        {primarySSNRequired && (
          <PLTextField
            type="tel"
            name="ssn"
            data-analytics="ssn-input"
            onChange={event => setSSN(event.target.rawValue)}
            onBlur={handleBlur}
            component="SSN"
            errors={validationErrors.ssn}
            autoFocus={!ssn}
            label={`${truncateLongName(primaryName.firstName)}’s SSN`}
            placeholder="###-##-####"
          />
        )}
        {showSentilinkSSNConsent && (
          <SentilinkAgreement checked={sentilinkConsent} handleCheckboxChange={onSentilinkChange} />
        )}
      </div>
      <div className="form__input">
        {isJoint && secondarySSNRequired && (
          <PLTextField
            type="tel"
            name="ssnSecondary"
            data-analytics="ssn-input"
            onChange={event => setSSNSecondary(event.target.rawValue)}
            onBlur={handleBlur}
            component="SSN"
            errors={validationErrors.ssnSecondary}
            autoFocus={!ssnSecondary && !primarySSNRequired}
            label={`${truncateLongName(secondaryName.firstName)}’s SSN`}
            placeholder="###-##-####"
          />
        )}
      </div>
    </Page>
  );
};

SSN.propTypes = {
  saveValues: func.isRequired,
  nextLocation: func.isRequired,
  isJoint: bool.isRequired,
  primarySSNRequired: bool,
  secondarySSNRequired: bool,
  primaryName: shape({
    firstName: string,
  }),
  secondaryName: shape({
    firstName: string,
  }),
  showSentilinkSSNConsent: bool,
};

SSN.defaultProps = {
  primarySSNRequired: false,
  secondarySSNRequired: false,
  primaryName: {},
  secondaryName: {},
  showSentilinkSSNConsent: false,
};

export default SSN;

require('/var/lib/jenkins/workspace/borrower-apply-ui_Release_0/src/pages/pi1-1QAAT/pages/SSN/SSN.less');